import React, { useState } from 'react';
import {
    X,
    Plus,
    Trash2,
    ChevronRight,
    ChevronLeft,
    CheckCircle,
    Building2,
    DollarSign,
    Calendar,
    MapPin,
    FileText,
    Users,
    AlertTriangle,
    Loader2,
    Info
} from 'lucide-react';
import { useLoanMutations, useCovenantMutations } from '../../hooks';
import { useAuth } from '../../context/AuthContext';

const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'NOK', 'SEK'];

const SECTORS = [
    'Manufacturing', 'Technology', 'Shipping', 'Financial Services',
    'Healthcare', 'Energy', 'Renewable Energy', 'Aerospace',
    'Hospitality', 'Logistics', 'Forestry', 'Retail', 'Real Estate'
];

const COUNTRIES = [
    'Germany', 'France', 'UK', 'Spain', 'Italy', 'Netherlands',
    'Switzerland', 'Norway', 'Sweden', 'Greece', 'Portugal',
    'Belgium', 'Austria', 'Ireland', 'Luxembourg', 'Estonia', 'USA'
];

const LEAD_ARRANGERS = [
    'Deutsche Bank', 'BNP Paribas', 'HSBC', 'Barclays', 'Société Générale',
    'DNB Bank', 'Commerzbank', 'Credit Suisse', 'Santander', 'ING Bank',
    'Crédit Agricole', 'Swedbank', 'UniCredit', 'Rabobank', 'Natixis'
];

const OPERATORS = ['<=', '>=', '=', '<', '>'];

const COVENANT_TYPES = [
    'Debt/EBITDA',
    'Interest Coverage',
    'Current Ratio',
    'Net Debt/Equity',
    'DSCR',
    'LTV Ratio',
    'Asset Coverage',
    'Minimum Cash',
    'Capex Limit',
    'Dividend Restriction',
    'Project Completion',
    'R&D Investment',
    'Occupancy Rate',
    'Fleet Utilization',
    'Order Backlog',
    'Custom'
];

const FACILITY_TYPES = [
    { value: 'term_loan_a', label: 'Term Loan A' },
    { value: 'term_loan_b', label: 'Term Loan B' },
    { value: 'rcf', label: 'Revolving Credit Facility' },
    { value: 'capex', label: 'Capex Facility' },
    { value: 'acquisition', label: 'Acquisition Facility' },
    { value: 'green_loan', label: 'Green Loan' },
    { value: 'sustainability_linked', label: 'Sustainability-Linked' },
    { value: 'bridge', label: 'Bridge Loan' }
];

const PARTICIPANT_ROLES = [
    { value: 'lead_arranger', label: 'Lead Arranger' },
    { value: 'co_arranger', label: 'Co-Arranger' },
    { value: 'bookrunner', label: 'Bookrunner' },
    { value: 'participant', label: 'Participant' },
    { value: 'sub_participant', label: 'Sub-Participant' }
];

const initialLoanData = {
    borrower: '',
    amount: '',
    currency: 'EUR',
    reference: '',
    bank: 'BNP Paribas',
    leadArranger: '',
    sector: '',
    country: '',
    startDate: '',
    maturityDate: '',
    nextReportingDate: '',
    notes: '',
    facilityType: 'term_loan_a',
    isGreenLoan: false,
    esgLinked: false,
    marginBps: '',
    commitmentFee: '',
    participants: []
};

const initialParticipant = {
    name: '',
    role: 'participant',
    amount: '',
    percentage: ''
};


const generateReference = () => {
    const year = new Date().getFullYear();
    const num = String(Math.floor(Math.random() * 900) + 100);
    return `LN-${year}-${num}`;
};

const initialCovenant = {
    name: '',
    customName: '',
    threshold: '',
    operator: '<=',
    current: '',
    frequency: 'quarterly'
};


const STEPS = [
    { id: 'basic', label: 'Basic Info', icon: Building2 },
    { id: 'facility', label: 'Facility & Syndicate', icon: Users },
    { id: 'covenants', label: 'Covenants', icon: AlertTriangle },
    { id: 'review', label: 'Review', icon: CheckCircle }
];

function AddLoanModal({ isOpen, onClose, onSubmit }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [loanData, setLoanData] = useState(initialLoanData);
    const [covenants, setCovenants] = useState([{ ...initialCovenant, id: 1 }]);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);


    const { createLoan } = useLoanMutations();
    const { createCovenant } = useCovenantMutations();
    const { user } = useAuth();

    const resetForm = () => {
        setCurrentStep(0);
        setLoanData(initialLoanData);
        setCovenants([{ ...initialCovenant, id: 1 }]);
        setErrors({});
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };


    const updateLoanData = (field, value) => {
        setLoanData(prev => ({ ...prev, [field]: value }));

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const addCovenant = () => {
        setCovenants(prev => [...prev, { ...initialCovenant, id: Date.now() }]);
    };

    const updateCovenant = (id, field, value) => {
        setCovenants(prev => prev.map(c =>
            c.id === id ? { ...c, [field]: value } : c
        ));
    };

    const removeCovenant = (id) => {
        if (covenants.length > 1) {
            setCovenants(prev => prev.filter(c => c.id !== id));
        }
    };


    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };


    const validateStep = (step) => {
        const newErrors = {};
        const today = getTodayDate();

        if (step === 0) {
            if (!loanData.borrower.trim()) newErrors.borrower = 'Borrower name is required';
            if (!loanData.amount) {
                newErrors.amount = 'Amount is required';
            } else if (parseFloat(loanData.amount) <= 0) {
                newErrors.amount = 'Amount must be greater than 0';
            }
            if (!loanData.leadArranger.trim()) newErrors.leadArranger = 'Lead arranger is required';
        }

        if (step === 1) {
            if (!loanData.sector) newErrors.sector = 'Sector is required';
            if (!loanData.country) newErrors.country = 'Country is required';
            if (!loanData.maturityDate) {
                newErrors.maturityDate = 'Maturity date is required';
            } else if (loanData.maturityDate <= today) {
                newErrors.maturityDate = 'Maturity date must be in the future';
            }
            if (loanData.startDate && loanData.maturityDate && loanData.startDate >= loanData.maturityDate) {
                newErrors.maturityDate = 'Maturity must be after start date';
            }

            if (loanData.nextReportingDate && loanData.nextReportingDate < today) {
                newErrors.nextReportingDate = 'Next reporting date must be today or in the future';
            }
        }

        if (step === 2) {
            const covenantErrors = [];
            covenants.forEach((cov, index) => {
                const covErrors = {};
                const name = cov.name === 'Custom' ? cov.customName : cov.name;
                if (!name.trim()) covErrors.name = 'Name is required';

                if (cov.threshold === '' || cov.threshold === null || isNaN(parseFloat(cov.threshold))) {
                    covErrors.threshold = 'Threshold is required';
                }
                if (cov.current === '' || cov.current === null || isNaN(parseFloat(cov.current))) {
                    covErrors.current = 'Current value is required';
                }
                if (Object.keys(covErrors).length > 0) covenantErrors[index] = covErrors;
            });
            if (Object.keys(covenantErrors).length > 0) newErrors.covenants = covenantErrors;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => Math.max(prev - 1, 0));
    };


    const handleSubmit = async () => {
        if (!validateStep(2)) {
            setCurrentStep(2);
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);

        try {

            const covenantStatuses = covenants.map(cov => calculateCovenantStatus(cov));
            const hasBreaches = covenantStatuses.some(s => s === 'critical');
            const hasWarnings = covenantStatuses.some(s => s === 'warning');
            const loanStatus = hasBreaches ? 'critical' : hasWarnings ? 'warning' : 'ok';


            const dbLoanData = {
                reference: loanData.reference || generateReference(),
                borrower: loanData.borrower.trim(),
                lead_arranger: loanData.leadArranger.trim(),
                bank: loanData.bank,
                amount: parseFloat(loanData.amount),
                currency: loanData.currency,
                start_date: loanData.startDate || new Date().toISOString().split('T')[0],
                maturity_date: loanData.maturityDate,
                next_reporting_date: loanData.nextReportingDate || loanData.maturityDate,
                sector: loanData.sector,
                country: loanData.country,
                status: loanStatus
            };


            const userId = user?.id || 1;
            const loanResult = await createLoan(dbLoanData, userId);

            if (!loanResult.success) {
                throw new Error(loanResult.error || 'Failed to create loan');
            }

            const loanId = loanResult.data.id;


            for (const cov of covenants) {
                const covenantName = cov.name === 'Custom' ? cov.customName : cov.name;
                const covenantStatus = calculateCovenantStatus(cov);

                const dbCovenantData = {
                    loan_id: loanId,
                    name: covenantName,
                    threshold: parseFloat(cov.threshold),
                    operator: cov.operator,
                    current_value: parseFloat(cov.current),
                    status: covenantStatus,
                    type: 'financial',
                    frequency: cov.frequency || 'quarterly'
                };

                const covenantResult = await createCovenant(dbCovenantData, userId);

                if (!covenantResult.success) {
                    console.error('Failed to create covenant:', covenantResult.error);
                }
            }


            const newLoan = {
                id: loanId,
                borrower: loanData.borrower.trim(),
                amount: parseFloat(loanData.amount),
                reference: dbLoanData.reference,
                bank: loanData.bank,
                currency: loanData.currency,
                leadArranger: loanData.leadArranger.trim(),
                sector: loanData.sector,
                country: loanData.country,
                startDate: dbLoanData.start_date,
                maturityDate: loanData.maturityDate,
                nextReportingDate: dbLoanData.next_reporting_date,
                notes: loanData.notes,
                status: loanStatus,
                covenants: covenants.map((cov, index) => ({
                    id: index + 1,
                    name: cov.name === 'Custom' ? cov.customName : cov.name,
                    threshold: parseFloat(cov.threshold),
                    operator: cov.operator,
                    current: parseFloat(cov.current),
                    status: calculateCovenantStatus(cov)
                }))
            };

            if (onSubmit) {
                onSubmit(newLoan);
            }

            handleClose();
        } catch (error) {
            console.error('Error creating loan:', error);
            setSubmitError(error.message || 'Failed to create loan');
        } finally {
            setIsSubmitting(false);
        }
    };


    const calculateCovenantStatus = (covenant) => {
        const current = parseFloat(covenant.current);
        const threshold = parseFloat(covenant.threshold);
        const operator = covenant.operator;

        let isBreached = false;
        let isWarning = false;

        if (operator === '<=') {
            isBreached = current > threshold;
            isWarning = current > threshold * 0.9;
        } else if (operator === '>=') {
            isBreached = current < threshold;
            isWarning = current < threshold * 1.1;
        } else if (operator === '<') {
            isBreached = current >= threshold;
            isWarning = current >= threshold * 0.9;
        } else if (operator === '>') {
            isBreached = current <= threshold;
            isWarning = current <= threshold * 1.1;
        } else {
            isBreached = current !== threshold;
        }

        if (isBreached) return 'critical';
        if (isWarning) return 'warning';
        return 'ok';
    };


    const formatAmount = (amount) => {
        if (!amount) return '';
        const num = parseFloat(amount);
        if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
        if (num >= 1e6) return `${(num / 1e6).toFixed(0)}M`;
        return num.toLocaleString();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">

                    {}
                    <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                                <Plus size={18} className="text-blue-600 sm:w-5 sm:h-5" />
                            </div>
                            <div>
                                <h2 className="text-base sm:text-lg font-semibold text-slate-800">Add New Loan</h2>
                                <p className="text-xs sm:text-sm text-slate-500">
                                    Step {currentStep + 1}/{STEPS.length}: {STEPS[currentStep].label}
                                </p>
                            </div>
                        </div>
                        <button onClick={handleClose} className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg">
                            <X size={18} className="text-slate-500 sm:w-5 sm:h-5" />
                        </button>
                    </div>

                    {}
                    <div className="px-4 sm:px-6 py-3 sm:py-4 bg-slate-50 border-b border-slate-200 overflow-x-auto">
                        <div className="flex items-center justify-between min-w-[300px]">
                            {STEPS.map((step, index) => {
                                const Icon = step.icon;
                                const isActive = index === currentStep;
                                const isCompleted = index < currentStep;

                                return (
                                    <React.Fragment key={step.id}>
                                        <div className="flex flex-col items-center">
                                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all ${
                                                isCompleted
                                                    ? 'bg-emerald-500 text-white'
                                                    : isActive
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-slate-200 text-slate-500'
                                            }`}>
                                                {isCompleted ? <CheckCircle size={16} className="sm:w-5 sm:h-5" /> : <Icon size={16} className="sm:w-5 sm:h-5" />}
                                            </div>
                                            <span className={`text-[10px] sm:text-xs mt-1 sm:mt-2 font-medium whitespace-nowrap ${
                                                isActive ? 'text-blue-600' : isCompleted ? 'text-emerald-600' : 'text-slate-500'
                                            }`}>
                        {step.label}
                      </span>
                                        </div>
                                        {index < STEPS.length - 1 && (
                                            <div className={`flex-1 h-1 mx-1 sm:mx-2 rounded ${
                                                isCompleted ? 'bg-emerald-500' : 'bg-slate-200'
                                            }`} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    {}
                    <div className="p-4 sm:p-6 max-h-[60vh] overflow-y-auto">

                        {}
                        <datalist id="lead-arrangers">
                            {LEAD_ARRANGERS.map(bank => (
                                <option key={bank} value={bank} />
                            ))}
                        </datalist>

                        {}
                        {currentStep === 0 && (
                            <div className="space-y-4 sm:space-y-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                    {}
                                    <div className="sm:col-span-2">
                                        <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                                            Borrower Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={loanData.borrower}
                                            onChange={(e) => updateLoanData('borrower', e.target.value)}
                                            placeholder="Enter company name"
                                            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                                                errors.borrower ? 'border-red-500 bg-red-50' : 'border-slate-300'
                                            }`}
                                        />
                                        {errors.borrower && (
                                            <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.borrower}</p>
                                        )}
                                    </div>

                                    {}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                                            Loan Amount <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={loanData.amount}
                                                onChange={(e) => updateLoanData('amount', e.target.value)}
                                                placeholder="100000000"
                                                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                                                    errors.amount ? 'border-red-500 bg-red-50' : 'border-slate-300'
                                                }`}
                                            />
                                            {loanData.amount && (
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs sm:text-sm text-slate-500">
                          {loanData.currency} {formatAmount(loanData.amount)}
                        </span>
                                            )}
                                        </div>
                                        {errors.amount && (
                                            <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.amount}</p>
                                        )}
                                    </div>

                                    {}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                                            Currency
                                        </label>
                                        <select
                                            value={loanData.currency}
                                            onChange={(e) => updateLoanData('currency', e.target.value)}
                                            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                        >
                                            {CURRENCIES.map(cur => (
                                                <option key={cur} value={cur}>{cur}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {}
                                    <div className="sm:col-span-2">
                                        <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                                            Lead Arranger <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            list="lead-arrangers"
                                            value={loanData.leadArranger}
                                            onChange={(e) => updateLoanData('leadArranger', e.target.value)}
                                            placeholder="Start typing or select from list..."
                                            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                                                errors.leadArranger ? 'border-red-500 bg-red-50' : 'border-slate-300'
                                            }`}
                                        />
                                        {errors.leadArranger && (
                                            <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.leadArranger}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {}
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                {}
                                <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                                    <h4 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
                                        <FileText size={18} className="text-blue-600" />
                                        Facility Details
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                                Facility Type <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={loanData.facilityType}
                                                onChange={(e) => updateLoanData('facilityType', e.target.value)}
                                                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            >
                                                {FACILITY_TYPES.map(type => (
                                                    <option key={type.value} value={type.value}>{type.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                                Sector <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={loanData.sector}
                                                onChange={(e) => updateLoanData('sector', e.target.value)}
                                                className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                                                    errors.sector ? 'border-red-500 bg-red-50' : 'border-slate-300'
                                                }`}
                                            >
                                                <option value="">Select sector</option>
                                                {SECTORS.map(sec => (
                                                    <option key={sec} value={sec}>{sec}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                                Country <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={loanData.country}
                                                onChange={(e) => updateLoanData('country', e.target.value)}
                                                className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                                                    errors.country ? 'border-red-500 bg-red-50' : 'border-slate-300'
                                                }`}
                                            >
                                                <option value="">Select country</option>
                                                {COUNTRIES.map(country => (
                                                    <option key={country} value={country}>{country}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                                Margin (bps)
                                            </label>
                                            <input
                                                type="number"
                                                value={loanData.marginBps}
                                                onChange={(e) => updateLoanData('marginBps', e.target.value)}
                                                placeholder="e.g. 250"
                                                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                                Start Date
                                            </label>
                                            <input
                                                type="date"
                                                value={loanData.startDate}
                                                onChange={(e) => updateLoanData('startDate', e.target.value)}
                                                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                                Maturity Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={loanData.maturityDate}
                                                onChange={(e) => updateLoanData('maturityDate', e.target.value)}
                                                className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                                                    errors.maturityDate ? 'border-red-500 bg-red-50' : 'border-slate-300'
                                                }`}
                                            />
                                        </div>
                                    </div>
                                    {}
                                    <div className="mt-4 flex flex-wrap gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={loanData.isGreenLoan}
                                                onChange={(e) => updateLoanData('isGreenLoan', e.target.checked)}
                                                className="w-4 h-4 text-green-600 rounded border-slate-300 focus:ring-green-500"
                                            />
                                            <span className="text-sm text-slate-700">Green Loan (LMA Principles)</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={loanData.esgLinked}
                                                onChange={(e) => updateLoanData('esgLinked', e.target.checked)}
                                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-slate-700">Sustainability-Linked</span>
                                        </label>
                                    </div>
                                </div>

                                {}
                                <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="font-medium text-slate-800 flex items-center gap-2">
                                            <Users size={18} className="text-purple-600" />
                                            Syndicate Participants
                                        </h4>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newParticipants = [...loanData.participants, { ...initialParticipant, id: Date.now() }];
                                                updateLoanData('participants', newParticipants);
                                            }}
                                            className="flex items-center gap-1 px-3 py-1.5 text-purple-600 hover:bg-purple-50 rounded-lg text-sm font-medium"
                                        >
                                            <Plus size={16} />
                                            Add Participant
                                        </button>
                                    </div>

                                    {loanData.participants.length === 0 ? (
                                        <div className="text-center py-6 text-slate-500 text-sm">
                                            <Users size={24} className="mx-auto mb-2 text-slate-300" />
                                            No participants added yet. Click "Add Participant" to add syndicate members.
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {loanData.participants.map((participant, index) => (
                                                <div key={participant.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                                                    <div className="flex-1 grid grid-cols-4 gap-3">
                                                        <input
                                                            type="text"
                                                            list="lead-arrangers"
                                                            value={participant.name}
                                                            onChange={(e) => {
                                                                const updated = [...loanData.participants];
                                                                updated[index].name = e.target.value;
                                                                updateLoanData('participants', updated);
                                                            }}
                                                            placeholder="Institution name"
                                                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                                        />
                                                        <select
                                                            value={participant.role}
                                                            onChange={(e) => {
                                                                const updated = [...loanData.participants];
                                                                updated[index].role = e.target.value;
                                                                updateLoanData('participants', updated);
                                                            }}
                                                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                                        >
                                                            {PARTICIPANT_ROLES.map(role => (
                                                                <option key={role.value} value={role.value}>{role.label}</option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            type="number"
                                                            value={participant.amount}
                                                            onChange={(e) => {
                                                                const updated = [...loanData.participants];
                                                                updated[index].amount = e.target.value;
                                                                if (loanData.amount) {
                                                                    updated[index].percentage = ((parseFloat(e.target.value) / parseFloat(loanData.amount)) * 100).toFixed(2);
                                                                }
                                                                updateLoanData('participants', updated);
                                                            }}
                                                            placeholder="Commitment"
                                                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                                        />
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={participant.percentage ? `${participant.percentage}%` : ''}
                                                                readOnly
                                                                placeholder="%"
                                                                className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const updated = loanData.participants.filter((_, i) => i !== index);
                                                                    updateLoanData('participants', updated);
                                                                }}
                                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {}
                                            {loanData.participants.length > 0 && (
                                                <div className="flex justify-end pt-2 border-t border-slate-200">
                                                    <div className="text-sm text-slate-600">
                                                        Total Commitment: <span className="font-semibold">
                                                            {loanData.currency} {formatAmount(loanData.participants.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0))}
                                                        </span>
                                                        <span className="text-slate-400 ml-2">
                                                            ({loanData.participants.reduce((sum, p) => sum + (parseFloat(p.percentage) || 0), 0).toFixed(1)}%)
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                            Next Reporting Date
                                        </label>
                                        <input
                                            type="date"
                                            value={loanData.nextReportingDate}
                                            onChange={(e) => updateLoanData('nextReportingDate', e.target.value)}
                                            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                            Commitment Fee (%)
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={loanData.commitmentFee}
                                            onChange={(e) => updateLoanData('commitmentFee', e.target.value)}
                                            placeholder="e.g. 0.35"
                                            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {}
                        {currentStep === 2 && (
                            <div className="space-y-4">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <Info size={18} className="text-blue-500 shrink-0" />
                                        <p className="text-xs sm:text-sm text-slate-600">
                                            Add at least one covenant. Status calculated automatically.
                                        </p>
                                    </div>
                                    <button
                                        onClick={addCovenant}
                                        className="flex items-center justify-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium"
                                    >
                                        <Plus size={18} />
                                        Add Covenant
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {covenants.map((covenant, index) => {
                                        const covErrors = errors.covenants?.[index] || {};

                                        return (
                                            <div key={covenant.id} className="border border-slate-200 rounded-xl p-3 sm:p-4 bg-slate-50">
                                                <div className="flex items-center justify-between mb-3 sm:mb-4">
                                                    <h4 className="font-medium text-slate-800 text-sm sm:text-base">Covenant {index + 1}</h4>
                                                    {covenants.length > 1 && (
                                                        <button
                                                            onClick={() => removeCovenant(covenant.id)}
                                                            className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                                                    {}
                                                    <div className={covenant.name === 'Custom' ? 'col-span-1' : 'col-span-2'}>
                                                        <label className="block text-xs font-medium text-slate-600 mb-1">
                                                            Type <span className="text-red-500">*</span>
                                                        </label>
                                                        <select
                                                            value={covenant.name}
                                                            onChange={(e) => updateCovenant(covenant.id, 'name', e.target.value)}
                                                            className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                                                covErrors.name ? 'border-red-500 bg-red-50' : 'border-slate-300'
                                                            }`}
                                                        >
                                                            <option value="">Select type</option>
                                                            {COVENANT_TYPES.map(type => (
                                                                <option key={type} value={type}>{type}</option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {}
                                                    {covenant.name === 'Custom' && (
                                                        <div>
                                                            <label className="block text-xs font-medium text-slate-600 mb-1">
                                                                Custom Name <span className="text-red-500">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={covenant.customName}
                                                                onChange={(e) => updateCovenant(covenant.id, 'customName', e.target.value)}
                                                                placeholder="Name"
                                                                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                                                    covErrors.name ? 'border-red-500 bg-red-50' : 'border-slate-300'
                                                                }`}
                                                            />
                                                        </div>
                                                    )}

                                                    {}
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-600 mb-1">
                                                            Operator
                                                        </label>
                                                        <select
                                                            value={covenant.operator}
                                                            onChange={(e) => updateCovenant(covenant.id, 'operator', e.target.value)}
                                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                                        >
                                                            {OPERATORS.map(op => (
                                                                <option key={op} value={op}>{op}</option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {}
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-600 mb-1">
                                                            Threshold <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={covenant.threshold}
                                                            onChange={(e) => updateCovenant(covenant.id, 'threshold', e.target.value)}
                                                            placeholder="4.0"
                                                            className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                                                covErrors.threshold ? 'border-red-500 bg-red-50' : 'border-slate-300'
                                                            }`}
                                                        />
                                                    </div>

                                                    {}
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-600 mb-1">
                                                            Current Value <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={covenant.current}
                                                            onChange={(e) => updateCovenant(covenant.id, 'current', e.target.value)}
                                                            placeholder="3.5"
                                                            className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                                                covErrors.current ? 'border-red-500 bg-red-50' : 'border-slate-300'
                                                            }`}
                                                        />
                                                    </div>
                                                </div>

                                                {}
                                                {covenant.threshold && covenant.current && (
                                                    <div className="mt-3 pt-3 border-t border-slate-200">
                                                        <span className="text-xs text-slate-500">Status: </span>
                                                        {(() => {
                                                            const status = calculateCovenantStatus(covenant);
                                                            return (
                                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                                                    status === 'ok'
                                                                        ? 'bg-emerald-100 text-emerald-700'
                                                                        : status === 'warning'
                                                                            ? 'bg-amber-100 text-amber-700'
                                                                            : 'bg-red-100 text-red-700'
                                                                }`}>
                                  {status === 'ok' ? 'Compliant' : status === 'warning' ? 'At Risk' : 'Breach'}
                                </span>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {}
                        {currentStep === 3 && (
                            <div className="space-y-6">
                                {}
                                <div className="border border-slate-200 rounded-xl p-5">
                                    <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                        <Building2 size={18} className="text-blue-600" />
                                        Loan Summary
                                    </h4>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-slate-500">Borrower:</span>
                                            <span className="ml-2 font-medium text-slate-800">{loanData.borrower}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Amount:</span>
                                            <span className="ml-2 font-medium text-slate-800">
                        {loanData.currency} {formatAmount(loanData.amount)}
                      </span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Lead Arranger:</span>
                                            <span className="ml-2 font-medium text-slate-800">{loanData.leadArranger}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Sector:</span>
                                            <span className="ml-2 font-medium text-slate-800">{loanData.sector}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Country:</span>
                                            <span className="ml-2 font-medium text-slate-800">{loanData.country}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Maturity:</span>
                                            <span className="ml-2 font-medium text-slate-800">
                        {new Date(loanData.maturityDate).toLocaleDateString('en-GB')}
                      </span>
                                        </div>
                                    </div>
                                </div>

                                {}
                                <div className="border border-slate-200 rounded-xl p-5">
                                    <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                        <AlertTriangle size={18} className="text-amber-600" />
                                        Covenants ({covenants.length})
                                    </h4>
                                    <div className="space-y-3">
                                        {covenants.map((covenant, index) => {
                                            const status = calculateCovenantStatus(covenant);
                                            const name = covenant.name === 'Custom' ? covenant.customName : covenant.name;

                                            return (
                                                <div key={covenant.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                                    <div>
                                                        <span className="font-medium text-slate-800">{name}</span>
                                                        <span className="text-slate-500 text-sm ml-2">
                              {covenant.operator} {covenant.threshold}
                            </span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-600">
                              Current: <strong>{covenant.current}</strong>
                            </span>
                                                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                                            status === 'ok'
                                                                ? 'bg-emerald-100 text-emerald-700'
                                                                : status === 'warning'
                                                                    ? 'bg-amber-100 text-amber-700'
                                                                    : 'bg-red-100 text-red-700'
                                                        }`}>
                              {status === 'ok' ? 'Compliant' : status === 'warning' ? 'At Risk' : 'Breach'}
                            </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {}
                    <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
                        {}
                        {submitError && (
                            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                {submitError}
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <button
                                onClick={handleBack}
                                disabled={currentStep === 0}
                                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={18} />
                                Back
                            </button>

                            {currentStep < STEPS.length - 1 ? (
                                <button
                                    onClick={handleNext}
                                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Next
                                    <ChevronRight size={18} />
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle size={18} />
                                            Create Loan
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddLoanModal;